/**
 * Prompts Service
 * API client for AI prompt management endpoints (IT Admin only)
 */

import { api } from './api';

export type PromptType = 'ocr' | 'audio';

export interface PromptData {
  type: PromptType;
  content: string;
  isDefault: boolean;
  updatedAt?: string;
  updatedBy?: number;
  updatedByEmail?: string;
}

export interface GetPromptsResponse {
  success: boolean;
  prompts: PromptData[];
}

export interface GetPromptResponse {
  success: boolean;
  type: PromptType;
  content: string;
  isDefault: boolean;
  updatedAt?: string;
  updatedBy?: number;
  updatedByEmail?: string;
}

export interface UpdatePromptResponse {
  success: boolean;
  message: string;
  type: PromptType;
  content: string;
  isDefault: boolean;
  updatedAt?: string;
  updatedBy?: number;
  updatedByEmail?: string;
}

export interface ResetPromptResponse {
  success: boolean;
  message: string;
  type: PromptType;
  content: string;
  isDefault: boolean;
}

export class PromptsService {
  /**
   * Get all prompts (both OCR and Audio)
   */
  static async getAllPrompts(): Promise<PromptData[]> {
    try {
      const response = await api.get<GetPromptsResponse>('/prompts');
      return response.prompts || [];
    } catch (error) {
      console.error('Error getting all prompts:', error);
      throw error;
    }
  }

  /**
   * Get a specific prompt by type
   */
  static async getPrompt(type: PromptType): Promise<PromptData> {
    try {
      const response = await api.get<GetPromptResponse>(`/prompts/${type}`);
      return {
        type: response.type,
        content: response.content,
        isDefault: response.isDefault,
        updatedAt: response.updatedAt,
        updatedBy: response.updatedBy,
        updatedByEmail: response.updatedByEmail,
      };
    } catch (error) {
      console.error(`Error getting ${type} prompt:`, error);
      throw error;
    }
  }

  /**
   * Update/create a custom prompt
   */
  static async setPrompt(type: PromptType, content: string): Promise<PromptData> {
    try {
      const response = await api.put<UpdatePromptResponse>(`/prompts/${type}`, { content });
      return {
        type: response.type,
        content: response.content,
        isDefault: response.isDefault,
        updatedAt: response.updatedAt,
        updatedBy: response.updatedBy,
        updatedByEmail: response.updatedByEmail,
      };
    } catch (error) {
      console.error(`Error setting ${type} prompt:`, error);
      throw error;
    }
  }

  /**
   * Reset a prompt to default (deletes custom prompt)
   */
  static async resetPrompt(type: PromptType): Promise<PromptData> {
    try {
      const response = await api.delete<ResetPromptResponse>(`/prompts/${type}`);
      return {
        type: response.type,
        content: response.content,
        isDefault: response.isDefault,
      };
    } catch (error) {
      console.error(`Error resetting ${type} prompt:`, error);
      throw error;
    }
  }

  /**
   * Get the default prompt from environment (for preview/comparison)
   */
  static async getDefaultPrompt(type: PromptType): Promise<string> {
    try {
      const response = await api.get<GetPromptResponse>(`/prompts/${type}/default`);
      return response.content;
    } catch (error) {
      console.error(`Error getting default ${type} prompt:`, error);
      throw error;
    }
  }
}
